import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Container, Form, Header, Icon, Label, Message, Modal, Popup } from 'semantic-ui-react';
import { Navigate, useNavigate } from 'react-router-dom';
import GenericTable from '../common/GenericTable';
import { useAuth } from '../context/AuthContext';
import { stockyApi } from '../misc/StockyApi';
import EditUserModal from './EditUserModal';
import {
  DEFAULT_USER_FILTERS,
  filterUsers,
  formatDueDate,
  getAccountState,
  getDueDateDetails,
  sortUsersByPriority,
} from './userFilters';
import { validateBillingFields, validateEmail } from './userForm';
import './usersDashboard.css';

const statusFilters = [
  { key: 'ALL', label: 'Todos' },
  { key: 'active', label: 'Activos' },
  { key: 'soon', label: 'Próximos a vencer' },
  { key: 'overdue', label: 'Vencidos' },
  { key: 'suspended', label: 'Suspendidos' },
  { key: 'blocked', label: 'Bloqueados por pago' },
];

const getApiErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join(' ') : message || fallback;
};

export function isValidDateOnly(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''));
  if (!match) return false;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.getUTCFullYear() === Number(year)
    && date.getUTCMonth() === Number(month) - 1
    && date.getUTCDate() === Number(day);
}

export function InlineField({ value, type = 'text', placeholder = '—', disabled, validate, onSave, display }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const committingRef = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [editing, value]);

  const cancel = () => {
    if (saving) return;
    setDraft(value ?? '');
    setError('');
    setEditing(false);
  };
  const commit = async (nextValue = draft) => {
    if (committingRef.current) return false;
    const validationError = validate?.(nextValue);
    if (validationError) {
      setError(validationError);
      return false;
    }
    if (String(nextValue ?? '') === String(value ?? '')) {
      cancel();
      return true;
    }
    committingRef.current = true;
    setSaving(true);
    try {
      const saved = await onSave(nextValue);
      if (saved) {
        setError('');
        setEditing(false);
      } else {
        // The parent restores its persisted row on a failed request. Keep that
        // value in the local draft too, so this cell never displays a false save.
        setDraft(value ?? '');
        setError('No se pudo guardar. Se restauró el valor anterior.');
      }
      return saved;
    } finally {
      committingRef.current = false;
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="admin-users__editable-value"
        disabled={disabled}
        onClick={() => {
          setDraft(value ?? '');
          setError('');
          setEditing(true);
        }}
        title="Editar"
      >
        {display ?? value ?? placeholder}
      </button>
    );
  }

  return (
    <div
      className="admin-users__inline-editor"
      onBlur={(event) => {
        // React bubbles blur. Moving focus inside this editor (including a
        // calendar control rendered here) is not an external commit.
        if (!event.currentTarget.contains(event.relatedTarget)) commit();
      }}
    >
      <input
        autoFocus
        type={type}
        value={draft}
        placeholder={placeholder}
        aria-label="Editar valor"
        aria-busy={saving}
        disabled={saving}
        onChange={(event) => {
          setDraft(event.target.value);
          setError('');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            cancel();
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
      {saving ? <span className="admin-users__inline-saving" role="status">Guardando…</span> : null}
      {error ? <span className="admin-users__inline-error">{error}</span> : null}
    </div>
  );
}

const featureState = (enabled, activeLabel = 'Habilitada', inactiveLabel = 'Desactivada') => {
  if (enabled === true) return { label: activeLabel, color: 'green' };
  if (enabled === false) return { label: inactiveLabel, color: undefined };
  return { label: 'Sin configurar', color: undefined };
};

export function UserFeatureControls({ row, disabled, onUpdate }) {
  const sync = featureState(row.syncEnabled, 'Activa', 'Pausada');
  const prices = featureState(row.bulkPriceUpdateEnabled, 'Activos', 'Desactivados');
  const copies = featureState(row.publishInAnotherAccountEnabled, 'Activa', 'Desactivado');
  const packs = featureState(row.packsEnabled, 'Activos', 'Desactivados');
  const kits = featureState(row.kitsEnabled, 'Activos', 'Desactivados');
  const skuImport = featureState(row.skuMigrationImportEnabled, 'Activa', 'Desactivada');
  return (
    <div className="admin-users__feature-controls" aria-label={`Configuración de ${row.username}`}>
      <Button type="button" size="mini" basic color={sync.color} disabled={disabled} onClick={() => onUpdate({ syncEnabled: row.syncEnabled !== true })}>
        Sincronización: {sync.label}
      </Button>
      <Button type="button" size="mini" basic color={prices.color} disabled={disabled} onClick={() => onUpdate({ bulkPriceUpdateEnabled: row.bulkPriceUpdateEnabled !== true })}>
        Precios: {prices.label}
      </Button>
      <Button type="button" size="mini" basic color={copies.color} disabled={disabled} onClick={() => onUpdate({ publishInAnotherAccountEnabled: row.publishInAnotherAccountEnabled !== true, publishInAnotherAccountFreeLimit: row.publishInAnotherAccountFreeLimit ?? 5 })}>
        Copiado: {copies.label}
      </Button>
      <Label size="mini" basic>Packs: {packs.label}</Label>
      <Label size="mini" basic>Kits: {kits.label}</Label>
      <Label size="mini" basic>Importación SKU: {skuImport.label}</Label>
    </div>
  );
}

function UsersDashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const isLoggedIn = auth.userIsAuthenticated();
  const isAdmin = auth.isAdmin();
  const accessToken = auth.user?.accessToken || auth.getUser()?.accessToken || '';
  const currentUserId = auth.getUser()?.data?.sub || auth.getUser()?.data?.id || '';
  const userAuthenticated = useMemo(() => (accessToken ? { accessToken } : null), [accessToken]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [column, setColumn] = useState('nextPaymentDueDate');
  const [direction, setDirection] = useState('ascending');
  const [filters, setFilters] = useState(DEFAULT_USER_FILTERS);
  const [savingUsername, setSavingUsername] = useState('');
  const [savingCell, setSavingCell] = useState('');
  const [notice, setNotice] = useState('');
  const [editingUsername, setEditingUsername] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingSuspension, setPendingSuspension] = useState(null);
  const [pendingPaymentBlock, setPendingPaymentBlock] = useState(null);

  const fetchUsers = async () => {
    if (!userAuthenticated) return;
    try {
      setLoading(true);
      const response = await stockyApi.getUsers(userAuthenticated);
      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setNotice(getApiErrorMessage(error, 'No se pudieron cargar los usuarios.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn && isAdmin && userAuthenticated) fetchUsers();
    // Authentication state is the intentional reload boundary; inline saves do not fetch the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isAdmin, userAuthenticated]);

  const saveInline = async (row, patch) => {
    const field = Object.keys(patch)[0];
    const originalUsername = row.username;
    const previous = { ...row };
    const cellId = `${row.id || originalUsername}:${field}`;
    if (!userAuthenticated || savingCell === cellId) return false;
    setSavingCell(cellId);
    try {
      const response = await stockyApi.updateAdminUser(userAuthenticated, originalUsername, patch);
      const saved = response?.data || patch;
      setRows((current) => current.map((item) => item.username === originalUsername ? { ...item, ...saved } : item));
      setNotice(previous.paymentBlockedAt && !saved.paymentBlockedAt
        ? 'Guardado. Se desbloqueó el acceso porque el vencimiento es posterior a hoy.'
        : 'Guardado');
      return true;
    } catch (error) {
      setRows((current) => current.map((item) => item.username === originalUsername ? previous : item));
      setNotice(getApiErrorMessage(error, 'No se pudo guardar el cambio. Se restauró el valor anterior.'));
      return false;
    } finally {
      setSavingCell((current) => current === cellId ? '' : current);
    }
  };

  // Blocking for an overdue payment is always a manual, confirmed admin action.
  const changePaymentBlock = async (row, block) => {
    if (!userAuthenticated || savingUsername) return;
    setSavingUsername(row.username);
    try {
      const call = block ? stockyApi.blockUserForPayment : stockyApi.unblockUserForPayment;
      const response = await call(userAuthenticated, row.username);
      const saved = response?.data || {};
      setRows((current) => current.map((item) => item.username === row.username
        ? { ...item, paymentStatus: saved.paymentStatus, paymentBlockedAt: saved.paymentBlockedAt ?? null }
        : item));
      setNotice(block ? `Se bloqueó el acceso de ${row.username} por pago vencido.` : `Se desbloqueó el acceso de ${row.username}.`);
    } catch (error) {
      setNotice(getApiErrorMessage(error, block ? 'No se pudo bloquear al usuario.' : 'No se pudo desbloquear al usuario.'));
    } finally {
      setSavingUsername('');
    }
  };

  // Employees take their status from the owner account (suspended / blocked for payment).
  const rowsWithOwnerState = useMemo(() => {
    const byId = new Map(rows.map((row) => [row.id, row]));
    return rows.map((row) => {
      if (!row.ownerUserId) return row;
      const ownerState = byId.has(row.ownerUserId) ? getAccountState(byId.get(row.ownerUserId)) : null;
      return { ...row, ownerAccountState: ownerState === 'suspended' || ownerState === 'blocked' ? ownerState : null };
    });
  }, [rows]);

  const sortedRows = useMemo(() => {
    const filtered = filterUsers(rowsWithOwnerState, filters);
    if (column === 'nextPaymentDueDate' && direction === 'ascending') return sortUsersByPriority(filtered);
    return [...filtered].sort((left, right) => {
      const leftValue = column === 'isActive' ? Number(left.isActive !== false) : left?.[column];
      const rightValue = column === 'isActive' ? Number(right.isActive !== false) : right?.[column];
      const result = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), 'es', { numeric: true });
      return direction === 'ascending' ? result : -result;
    });
  }, [rowsWithOwnerState, filters, column, direction]);

  const countByState = useMemo(() => rowsWithOwnerState.reduce((counts, row) => {
    const state = getAccountState(row);
    counts[state] = (counts[state] || 0) + 1;
    return counts;
  }, {}), [rowsWithOwnerState]);
  const editingRow = rows.find((row) => row.username === editingUsername) || null;
  const suspensionEmployees = pendingSuspension
    ? rows.filter((row) => row.ownerUserId === pendingSuspension.id).length
    : 0;

  const handleSort = (clickedColumn) => {
    if (column !== clickedColumn) {
      setColumn(clickedColumn);
      setDirection('ascending');
      return;
    }
    setDirection((current) => current === 'ascending' ? 'descending' : 'ascending');
  };

  const saveUserEdits = async ({ profile, billing }) => {
    if (!userAuthenticated || !editingRow) return { ok: false };
    let profileSaved = false;
    try {
      setSavingEdit(true);
      if (Object.keys(profile).length) {
        await stockyApi.updateUserProfile(userAuthenticated, editingRow.username, profile);
        profileSaved = true;
      }
      if (Object.keys(billing).length) await stockyApi.updateUserBilling(userAuthenticated, editingRow.username, billing);
      await fetchUsers();
      return { ok: true };
    } catch (error) {
      await fetchUsers();
      const reason = getApiErrorMessage(error, 'No se pudieron guardar los cambios.');
      return { ok: false, message: profileSaved ? `Se guardaron los datos de usuario, pero no los de pago: ${reason}` : reason };
    } finally {
      setSavingEdit(false);
    }
  };

  const updateControl = async (row, payload) => {
    if (!userAuthenticated || savingUsername) return false;
    setSavingUsername(row.username);
    try {
      const response = await stockyApi.updateUserSyncControls(userAuthenticated, row.username, payload);
      const saved = response?.data || {};
      // Only merge the controls just changed. Other flag values may be absent
      // from an older account and must not turn into a misleading false default.
      const savedPatch = Object.keys(payload).reduce((patch, key) => {
        patch[key] = saved[key] !== undefined ? saved[key] : payload[key];
        return patch;
      }, {});
      setRows((current) => current.map((item) => item.username === row.username ? { ...item, ...savedPatch } : item));
      setNotice('Configuración actualizada');
      return true;
    } catch (error) {
      setNotice(getApiErrorMessage(error, 'No se pudo actualizar la configuración.'));
      return false;
    } finally {
      setSavingUsername('');
    }
  };

  const openWhatsAppReminder = (row) => {
    const phone = String(row.phoneNumber || '').replace(/[^0-9]/g, '');
    if (!phone) {
      setNotice('Este usuario no tiene un teléfono cargado.');
      return;
    }
    const amount = row.nextPaymentAmount == null ? 'a confirmar' : `$${Number(row.nextPaymentAmount).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
    const due = formatDueDate(row.nextPaymentDueDate);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Hola ${row.username}, te recordamos que tenés un pago pendiente de ${amount} con vencimiento el ${due}.`)}`, '_blank');
  };

  if (!isLoggedIn || !isAdmin) return <Navigate to="/home" replace />;

  const inlineDisabled = (row, field) =>
    savingUsername === row.username
    || loading
    || (field ? savingCell === `${row.id || row.username}:${field}` : false);
  // Payment data belongs to the owner account: an employee row is read-only for it.
  const billingDisabled = (row, field) => inlineDisabled(row, field) || Boolean(row.ownerUserId);
  const columnsConfig = [
    {
      key: 'username', label: 'Usuario / Nombre', width: '16%',
      render: (value, row) => <InlineField value={value} disabled={inlineDisabled(row, 'username')} validate={(next) => String(next).trim() ? null : 'Ingresá un usuario.'} onSave={(next) => saveInline(row, { username: String(next).trim() })} />,
    },
    {
      key: 'ownerUsername',
      label: 'Cuenta dueña',
      sortable: false,
      width: '10%',
      render: (value) => value || '-',
    },
    {
      key: 'email', label: 'Email', width: '20%',
      render: (value, row) => <InlineField value={value} type="email" disabled={inlineDisabled(row, 'email')} validate={validateEmail} onSave={(next) => saveInline(row, { email: String(next).trim() })} />,
    },
    {
      // Copies of items to another account: used / allowed. The allowed number is edited inline.
      key: 'publishInAnotherAccountFreeLimit', label: 'Cuota de copias', width: '10%',
      render: (value, row) => {
        if (row.ownerUserId) return <span className="admin-users__muted">—</span>;
        const configured = row.publishInAnotherAccountEnabled !== null && row.publishInAnotherAccountEnabled !== undefined;
        const used = configured ? Number(row.publishInAnotherAccountCopiesUsed || 0) : null;
        const limit = configured && value !== null && value !== undefined ? Number(value) : '';
        return (
          <div className="admin-users__due-cell">
            <InlineField
              value={limit}
              type="number"
              disabled={inlineDisabled(row)}
              display={configured && limit !== '' ? `${used} / ${limit}` : '—'}
              validate={(next) => (Number.isInteger(Number(next)) && Number(next) >= 0 && String(next).trim() !== '' ? null : 'Ingresá un número entero desde 0.')}
              onSave={(next) => updateControl(row, { publishInAnotherAccountFreeLimit: Number(next) })}
            />
          </div>
        );
      },
    },
    {
      key: 'featureControls', label: 'Funciones', sortable: false, width: '24%',
      render: (_, row) => <UserFeatureControls row={row} disabled={inlineDisabled(row)} onUpdate={(payload) => updateControl(row, payload)} />,
    },
    {
      key: 'nextPaymentAmount', label: 'Monto a pagar', width: '12%',
      render: (value, row) => <InlineField value={value ?? ''} type="number" placeholder="—" disabled={billingDisabled(row, 'nextPaymentAmount')} display={value == null ? '—' : `$${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`} validate={(next) => validateBillingFields({ nextPaymentAmount: next })} onSave={(next) => saveInline(row, { nextPaymentAmount: String(next).trim() === '' ? null : Number(next) })} />,
    },
    {
      key: 'nextPaymentDueDate', label: 'Vencimiento', width: '18%',
      getCellStyle: (value) => {
        const state = getDueDateDetails(value).state;
        return state === 'overdue' ? { background: '#FDECEC', color: '#B42318' } : state === 'soon' ? { background: '#FFF3E0', color: '#B54708' } : undefined;
      },
      render: (value, row) => {
        const details = getDueDateDetails(value);
        // Employees follow their owner and admins are never blocked, so neither gets the action.
        const canManageBlock = row.role !== 'ADMIN' && !row.ownerUserId;
        const blocked = Boolean(row.paymentBlockedAt);
        const overdue = row.paymentStatus === 'overdue';
        return (
          <div className="admin-users__due-cell">
            <Popup content={details.hint} trigger={<span><InlineField value={value || ''} type="date" disabled={billingDisabled(row, 'nextPaymentDueDate')} display={formatDueDate(value)} validate={(next) => isValidDateOnly(next) ? null : 'Ingresá una fecha válida.'} onSave={(next) => saveInline(row, { nextPaymentDueDate: next })} /></span>} />
            {canManageBlock && blocked ? (
              <Button type="button" size="mini" basic color="green" disabled={inlineDisabled(row)} onClick={() => changePaymentBlock(row, false)}><Icon name="unlock" />Desbloquear</Button>
            ) : null}
            {canManageBlock && !blocked && overdue ? (
              <Button type="button" size="mini" negative disabled={inlineDisabled(row)} onClick={() => setPendingPaymentBlock(row)}><Icon name="lock" />Bloquear por vencido</Button>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'accountState', label: 'Estado', sortable: false, width: '10%',
      render: (_, row) => {
        const state = getAccountState(row);
        const labels = { active: 'Activo', soon: 'Próximo a vencer', overdue: 'Vencido', suspended: 'Suspendido', blocked: 'Bloqueado por pago' };
        const colors = { active: 'green', soon: 'orange', overdue: 'red', suspended: 'grey', blocked: 'black' };
        const viaOwner = row.ownerUserId && (state === 'suspended' || state === 'blocked') && row.isActive !== false;
        return <Label basic color={colors[state]} title={viaOwner ? 'Por el estado de su cuenta dueña' : undefined}>{labels[state]}{viaOwner ? ' (cuenta dueña)' : ''}</Label>;
      },
    },
    {
      key: 'isActive', label: 'Usuario activo', width: '9%',
      render: (value, row) => <Form.Checkbox toggle checked={value !== false} disabled={inlineDisabled(row) || row.id === currentUserId} label={value !== false ? 'Activo' : 'Suspendido'} onChange={(_, data) => data.checked ? saveInline(row, { isActive: true }) : setPendingSuspension(row)} />,
    },
  ];

  const actions = [
    {
      icon: 'user plus', color: 'purple', title: 'Agregar cuenta hija (empleado) a esta cuenta',
      disabled: (row) => inlineDisabled(row) || Boolean(row.ownerUserId) || row.isActive === false,
      onClick: (row) => navigate(`/signup?owner=${encodeURIComponent(row.username)}`),
    },
    { icon: 'edit', color: 'teal', title: 'Editar configuración avanzada', disabled: (row) => inlineDisabled(row), onClick: (row) => setEditingUsername(row.username) },
    { icon: 'whatsapp', color: 'green', title: 'Enviar recordatorio por WhatsApp', disabled: (row) => inlineDisabled(row), onClick: openWhatsAppReminder },
  ];

  return (
    <Container fluid className="admin-users">
      <div className="admin-users__heading">
        <div><Header as="h2">Usuarios</Header><p>Administrá accesos y vencimientos sin salir de la grilla.</p></div>
        <Button color="purple" onClick={() => navigate('/signup')}><Icon name="user plus" />Alta de usuario</Button>
      </div>
      {notice ? <Message size="small" onDismiss={() => setNotice('')} content={notice} /> : null}
      <div className="admin-users__filters">
        <Form.Input icon="search" iconPosition="left" placeholder="Buscar nombre, email o teléfono" value={filters.search} onChange={(_, { value }) => setFilters((current) => ({ ...current, search: value }))} />
        <div className="admin-users__filter-pills" role="group" aria-label="Filtrar usuarios">
          {statusFilters.map((filter) => {
            const count = filter.key === 'ALL' ? rows.length : (countByState[filter.key] || 0);
            return <Button key={filter.key} size="tiny" basic={filters.status !== filter.key} color={filters.status === filter.key ? 'violet' : undefined} onClick={() => setFilters((current) => ({ ...current, status: filter.key }))}>{filter.label} <Label circular size="mini">{count}</Label></Button>;
          })}
        </div>
      </div>
      <div className="admin-users__summary">Mostrando {sortedRows.length} de {rows.length} usuarios. Hacé clic en un dato para editarlo.</div>
      <GenericTable column={column} direction={direction} onSort={handleSort} columnsConfig={columnsConfig} data={sortedRows} showCheckbox={false} actions={actions} rowClickable={false} getRowId={(row) => row.id || row.username} tableMinWidth="1550px" tableStyle={{ border: 'none' }} tableContainerStyle={{ borderRadius: 12, border: '1px solid #EAECF0', overflow: 'auto' }} emptyMessage={loading ? 'Cargando usuarios...' : 'Sin usuarios'} />
      <EditUserModal open={Boolean(editingRow)} row={editingRow} isSelf={editingRow?.id === currentUserId} saving={savingEdit} onClose={() => setEditingUsername('')} onSave={saveUserEdits} />
      <Modal size="tiny" open={Boolean(pendingPaymentBlock)} onClose={() => !savingUsername && setPendingPaymentBlock(null)}>
        <Modal.Header>¿Bloquear a {pendingPaymentBlock?.username} por pago vencido?</Modal.Header>
        <Modal.Content>El usuario y sus empleados no podrán ingresar a MercuryCom y verán cómo regularizar el pago. Sus datos no se eliminan y podés desbloquearlo cuando quieras.</Modal.Content>
        <Modal.Actions><Button onClick={() => setPendingPaymentBlock(null)} disabled={Boolean(savingUsername)}>Cancelar</Button><Button negative onClick={async () => { const row = pendingPaymentBlock; setPendingPaymentBlock(null); if (row) await changePaymentBlock(row, true); }} disabled={Boolean(savingUsername)}>Bloquear acceso</Button></Modal.Actions>
      </Modal>
      <Modal size="tiny" open={Boolean(pendingSuspension)} onClose={() => !savingUsername && setPendingSuspension(null)}>
        <Modal.Header>¿Suspender acceso de {pendingSuspension?.username}?</Modal.Header>
        <Modal.Content>{suspensionEmployees > 0 ? `El usuario y sus ${suspensionEmployees} ${suspensionEmployees === 1 ? 'empleado no podrán' : 'empleados no podrán'}` : 'El usuario no podrá'} acceder a MercuryCom hasta que vuelvas a activarlo. Sus datos no se eliminarán.</Modal.Content>
        <Modal.Actions><Button onClick={() => setPendingSuspension(null)} disabled={Boolean(savingUsername)}>Cancelar</Button><Button negative onClick={async () => { const row = pendingSuspension; setPendingSuspension(null); if (row) await saveInline(row, { isActive: false }); }} loading={Boolean(savingUsername)} disabled={Boolean(savingUsername)}>Suspender acceso</Button></Modal.Actions>
      </Modal>
    </Container>
  );
}

export default UsersDashboard;
